import type { DragItem, Key } from 'react-aria';

import type * as api from '@gitbook/api-client';
import { useDragAndDrop } from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { addSectionsToGroup } from './addSectionsToGroup';

export interface UseSiteStructureTreeDragAndDropProps {
    getItem: (key: Key) => api.SiteSection | api.SiteSectionGroup | api.SiteSpace | null;
    getItemIndex: (key: Key) => number;
    checkIsSectionInGroup: (siteSection: api.SiteSection) => boolean;
    draggedKeys: Set<Key>;
    setDraggedKeys(keys: Set<Key>): void;
    expandedKeys: Set<Key>;
    setExpandedKeys(keys: Set<Key>): void;
    organization: api.Organization;
    site: api.Site;
    onReorder: (props: {
        movedItem: api.SiteSection | api.SiteSectionGroup | api.SiteSpace;
        targetItem: api.SiteSection | api.SiteSectionGroup | api.SiteSpace;
        position: 'before' | 'after';
    }) => void;
    isDisabled?: boolean;
}

/**
 * Drag and drop operations hook for site structure reordering
 */
export function useSiteStructureTreeDragAndDrop(props: UseSiteStructureTreeDragAndDropProps) {
    const {
        getItem,
        getItemIndex,
        checkIsSectionInGroup,
        draggedKeys,
        setDraggedKeys,
        expandedKeys,
        setExpandedKeys,
        organization,
        site,
        onReorder,
        isDisabled,
    } = props;
    return useDragAndDrop({
        isDisabled,
        onDragStart: (event) => {
            setDraggedKeys(event.keys);
        },
        onDragEnd: () => {
            setDraggedKeys(new Set());
        },
        // Open the item when we drag over it.
        onDropActivate: (event) => {
            // If we drop on an item, select it.
            if (event.target.type === 'item' && event.target.dropPosition === 'on') {
                // If not already expanded.
                if (!expandedKeys.has(event.target.key)) {
                    setExpandedKeys(new Set(expandedKeys).add(event.target.key));
                }
            }
        },
        getItems: (keys) => {
            return Array.from(keys).map((key) => {
                const item = getItem(key);
                assert(item, `Item with key ${key} not found`);
                const data: DragItem = {
                    [`x-gitbook/${item.object}`]: String(item.id),
                    'text/plain': item.title,
                };
                return data;
            });
        },
        getAllowedDropOperations: () => ['move'],
        acceptedDragTypes: [
            'x-gitbook/site-section-group',
            'x-gitbook/site-section',
            'x-gitbook/site-space',
        ],
        getDropOperation(target, _types) {
            const draggedItems = Array.from(draggedKeys)
                .map((key) => getItem(key))
                .filter((x) => x !== null);

            // No valid items dragged, cancel the operation.
            if (draggedItems.length === 0) {
                return 'cancel';
            }

            // We only support dropping on items.
            if (target.type !== 'item') {
                return 'cancel';
            }

            // If we want to drag just after an expanded item, cancel the operation.
            if (target.dropPosition === 'after' && expandedKeys.has(target.key)) {
                return 'cancel';
            }

            const targetItem = getItem(target.key);

            // Not a valid target, cancel the operation.
            if (!targetItem) {
                return 'cancel';
            }

            if (target.dropPosition === 'on') {
                // Only allow dropping on site section groups.
                if (targetItem.object !== 'site-section-group') {
                    return 'cancel';
                }

                // Only site sections can be dropped on a site section group.
                if (draggedItems.some((item) => item.object !== 'site-section')) {
                    return 'cancel';
                }

                // If all dragged items are already in the target group, cancel the operation.
                if (
                    draggedItems.every(
                        (item) =>
                            item.object === 'site-section' && item.sectionGroup === targetItem.id
                    )
                ) {
                    return 'cancel';
                }

                return 'move';
            }

            const singleDraggedItem = draggedItems.length === 1 ? draggedItems[0] : null;

            // We can only move one item at a time.
            // It could be possible but we want to ship fast, so, let's keep it simple.
            if (!singleDraggedItem) {
                return 'cancel';
            }

            // Determine the rules based on the target item type.
            switch (targetItem.object) {
                case 'site-section-group': {
                    break;
                }
                case 'site-section': {
                    // If section is part of a group.
                    if (checkIsSectionInGroup(targetItem)) {
                        // We can only sections/groups before and after.
                        if (
                            singleDraggedItem.object !== 'site-section' &&
                            singleDraggedItem.object !== 'site-section-group'
                        ) {
                            return 'cancel';
                        }
                    }

                    // Allow to move if the section are in different groups / levels.
                    if (
                        singleDraggedItem.object === 'site-section' &&
                        targetItem.sectionGroup !== singleDraggedItem.sectionGroup
                    ) {
                        return 'move';
                    }

                    break;
                }
                case 'site-space': {
                    // We can only move sections and groups before and after first-level sections.
                    if (singleDraggedItem.object !== 'site-space') {
                        return 'cancel';
                    }

                    // We can only move sections and groups before and after first-level sections.
                    if (targetItem.section !== singleDraggedItem.section) {
                        return 'cancel';
                    }
                    break;
                }
                default:
                    assertNever(targetItem);
            }

            const targetIndex = getItemIndex(target.key);
            const draggedIndex = getItemIndex(singleDraggedItem.id);
            const resultIndex =
                target.dropPosition === 'before' ? targetIndex - 1 : targetIndex + 1;

            if (targetIndex === draggedIndex || resultIndex === draggedIndex) {
                return 'cancel';
            }

            return 'move';
        },
        onItemDrop: async (event) => {
            if (!event.isInternal) {
                return;
            }

            if (event.dropOperation !== 'move') {
                return;
            }

            const targetItem = getItem(event.target.key);

            // We only support site section groups as targets.
            if (targetItem?.object !== 'site-section-group') {
                return;
            }

            const keys = (
                await Promise.all(
                    event.items.map(async (item) => {
                        if (item.kind !== 'text') {
                            return null;
                        }
                        // We only support site sections.
                        if (!item.types.has('x-gitbook/site-section')) {
                            return null;
                        }
                        const text = await item.getText('x-gitbook/site-section');
                        return text;
                    })
                )
            ).filter((x) => x !== null);

            const siteSections = keys
                .map((key) => getItem(key))
                .filter((x) => x?.object === 'site-section');

            if (siteSections.length === 0) {
                return;
            }

            addSectionsToGroup({
                sectionGroup: targetItem,
                siteSections,
                organization,
                site,
            });
        },
        onReorder: (event) => {
            // We only support move operations
            if (event.dropOperation !== 'move') {
                return;
            }

            // Handled by onItemDrop
            if (event.target.dropPosition === 'on') {
                return;
            }

            const targetItem = getItem(event.target.key);

            // No valid target item, bail.
            if (!targetItem) {
                return;
            }

            const movedItems = Array.from(event.keys)
                .map((key) => getItem(key))
                .filter((x) => x !== null);

            // No valid items moved, bail.
            if (movedItems.length === 0) {
                return;
            }

            const movedItem = movedItems.length === 1 ? movedItems[0] : null;

            // We only support moving one item at a time.
            if (!movedItem) {
                return;
            }

            onReorder({
                movedItem,
                targetItem,
                position: event.target.dropPosition,
            });
        },
    });
}
